package dev.kuml.portal.web.i18n.format

/**
 * R-18 §5.10 -- language-dependent FORMATTING (not fixed-vocabulary strings): dates, month names,
 * decimal separators, byte-size labels, plan labels. Pure, DOM-free, over the same ISO-8601
 * instant strings `util/DateFormat.kt` already parsed (`kotlin.time.Instant.toString()`, always
 * UTC) -- no `kotlinx-datetime` dependency needed, exactly as `DateFormat.kt`'s own KDoc explains.
 *
 * The English date format is deliberately ISO `YYYY-MM-DD`, not `MM/DD/YYYY` or `DD/MM/YYYY` --
 * see this wave's plan, decision E-8: it is the only unambiguous form for an internationally used
 * developer tool and sidesteps the US/UK ordering trap entirely.
 */
public interface LocaleFormat {
    /** `null` if [isoInstant] does not look like an ISO instant (mirrors
     * [dev.kuml.portal.web.util.DateFormat.timeOfDay]'s defensive `null`-on-malformed-input
     * contract). */
    public fun date(isoInstant: String): String?

    public fun dateFromYmd(
        year: Int,
        month: Int,
        day: Int,
    ): String

    /** `"HH:mm"` -- deliberately IDENTICAL in both languages (24-hour clock reads the same either
     * way; there is no "German vs. English" convention worth diverging over for a bare time). */
    public fun timeOfDay(isoInstant: String): String? {
        val tIndex = isoInstant.indexOf('T')
        if (tIndex < 0 || isoInstant.length < tIndex + 6) return null
        return isoInstant.substring(tIndex + 1, tIndex + 6)
    }

    public fun monthShort(month1Based: Int): String

    /** German-style `"12,4 KB"` vs. English-style `"12.4 KB"` -- same tenths-of-a-KB integer math
     * as the pre-R-18 `AiContextFile.sizeLabel`, only the separator character differs. */
    public fun sizeLabel(bytes: Int): String

    public fun planLabel(planId: String): String
}

private fun sizeLabelWith(
    bytes: Int,
    decimalSeparator: Char,
): String {
    if (bytes < 1_000) return "$bytes B"
    val tenths = kotlin.math.round(bytes / 100.0).toInt()
    val whole = tenths / 10
    val fraction = tenths % 10
    return "$whole$decimalSeparator$fraction KB"
}

public object LocaleFormatDe : LocaleFormat {
    private val monthNames = listOf("Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez")

    override fun date(isoInstant: String): String? {
        val tIndex = isoInstant.indexOf('T')
        if (tIndex < 10) return null
        val parts = isoInstant.substring(0, tIndex).split("-")
        if (parts.size != 3) return null
        return "${parts[2]}.${parts[1]}.${parts[0]}"
    }

    override fun dateFromYmd(
        year: Int,
        month: Int,
        day: Int,
    ): String = "${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.$year"

    override fun monthShort(month1Based: Int): String = monthNames.getOrElse(month1Based - 1) { "" }

    override fun sizeLabel(bytes: Int): String = sizeLabelWith(bytes, ',')

    override fun planLabel(planId: String): String =
        when (planId) {
            "free" -> "Free-Plan"
            "pro" -> "Pro-Plan"
            "team" -> "Team-Plan"
            else -> planId.replaceFirstChar { c -> c.uppercase() } + "-Plan"
        }
}

public object LocaleFormatEn : LocaleFormat {
    private val monthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

    override fun date(isoInstant: String): String? {
        val tIndex = isoInstant.indexOf('T')
        if (tIndex < 10) return null
        val parts = isoInstant.substring(0, tIndex).split("-")
        if (parts.size != 3) return null
        return "${parts[0]}-${parts[1]}-${parts[2]}"
    }

    override fun dateFromYmd(
        year: Int,
        month: Int,
        day: Int,
    ): String = "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"

    override fun monthShort(month1Based: Int): String = monthNames.getOrElse(month1Based - 1) { "" }

    override fun sizeLabel(bytes: Int): String = sizeLabelWith(bytes, '.')

    override fun planLabel(planId: String): String =
        when (planId) {
            "free" -> "Free plan"
            "pro" -> "Pro plan"
            "team" -> "Team plan"
            else -> planId.replaceFirstChar { c -> c.uppercase() } + " plan"
        }
}
